import { Box, Inline, xcss } from '@atlaskit/primitives';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseField, DatabaseValue } from '@databases/types/database';
import { convertType, isEmptyValue } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useMemo } from 'react';
import styled from 'styled-components';

import FieldTypeIcon from '../database-field/FieldTypeIcon';
import { FIELD_TYPES } from '../field-types/fieldTypes';

import { DatabaseValueViewContainer, useLoadDependencies } from './utils';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    display: block;
    min-width: 0;
  }
`;

const fieldNamePreviewContainerStyles = xcss({
  gap: 'space.100',
  color: 'color.text.subtlest',
  alignItems: 'center',
});

const fieldNamePreviewStyles = xcss({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  gap: 'space.100',
  color: 'color.text.subtlest',
});

export interface DatabaseValueViewProps
  extends Omit<InternalDatabaseValueViewProps, 'field' | 'value'> {
  contentId: string;
  fieldId: string;
  entryId: string;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueView: FunctionComponent<DatabaseValueViewProps> = ({
  contentId,
  fieldId,
  entryId,
  showFieldNamePlaceholder,
  ...props
}) => {
  const scope = useScope();
  const [field, value] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    const field = getDatabaseFieldRequired(databaseDraft, fieldId);
    const value = getConvertedDatabaseValue(databaseDraft, field, entry, state);
    return [field, value] as const;
  });

  return (
    <InternalDatabaseValueView
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      showFieldNamePlaceholder={showFieldNamePlaceholder}
    />
  );
};

export default React.memo(DatabaseValueView);

export interface InternalDatabaseValueViewProps {
  contentId: string;
  entryId?: string;
  field: DatabaseField;
  value: DatabaseValue;
  showPlaceholder?: boolean;
  noFocus?: boolean;
  recursionCount?: number;
  isReadonly?: boolean;
  /** If this is true, will show the field name as a placeholder for empty values. */
  showFieldNamePlaceholder?: boolean;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
export const InternalDatabaseValueView: FunctionComponent<
  InternalDatabaseValueViewProps
> = ({
  contentId,
  entryId,
  field,
  value,
  showPlaceholder,
  noFocus,
  recursionCount = 0,
  isReadonly,
  showFieldNamePlaceholder,
}) => {
  const Component = FIELD_TYPES[field.type].view as any;
  if (!Component) {
    throw new Error(`Unknown field type ${field.type}.`);
  }

  const store = defaultStoreUtils.useStore();
  const shouldShowFieldNamePlaceholder = defaultStoreUtils.useEqualSelector((state) => {
    if (showFieldNamePlaceholder && entryId) {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const entry = getDatabaseEntryRequired(database, entryId);
      return isEmptyValue({ database, field, entry, value }, state);
    }
    return false;
  });

  // Not using useSelector() here, as some conversions generate random IDs, meaning that the converted value changes each time.
  // This way we only run the conversion when the original value changes.
  const convertedValue = useMemo(() => {
    return convertType({ field, value }, field.type, store.getState());
  }, [field, store, value]);

  useLoadDependencies({ contentId, entryId, field, convertedValue }, store.getState());

  if (shouldShowFieldNamePlaceholder) {
    return (
      <Inline xcss={fieldNamePreviewContainerStyles}>
        <FieldTypeIcon type={field.type} noTooltip />
        <Box xcss={fieldNamePreviewStyles}>{field.name}</Box>
      </Inline>
    );
  }

  return (
    <DatabaseValueViewContainer data-testid="database-value-view-container">
      <StyledSkeletonItem data-testid="database-value-view-skeleton-item" minWidth={80}>
        <Component
          contentId={contentId}
          entryId={entryId}
          field={field}
          value={convertedValue.value}
          showPlaceholder={showPlaceholder}
          noFocus={noFocus}
          recursionCount={recursionCount}
          isReadonly={isReadonly}
        />
      </StyledSkeletonItem>
    </DatabaseValueViewContainer>
  );
};
